import { StateCreator } from "zustand";
import { GlobalState } from "../globalStore";
import type {
  MindstringPrototype,
  SituationCategory,
  SupportedLanguage,
} from "@/core/fastapiClient/sdk";

export interface SupportedLanguageState {
  data: SupportedLanguage[];
  isLoading: boolean;
  error: string | null;
}

export interface MindstringPrototypeState {
  data: MindstringPrototype[];
  isLoading: boolean;
  error: string | null;
}

export interface SituationCategoriesState {
  data: SituationCategory[];
  isLoading: boolean;
  error: string | null;
}

export interface PrototypeToCategoryMapState {
  data: Record<MindstringPrototype, SituationCategory> | null;
  isLoading: boolean;
  error: string | null;
}

export interface SsotSlice {
  //////* Languages
  languages: SupportedLanguageState;
  setLanguages: (languages: SupportedLanguage[]) => void;
  setLanguagesLoading: () => void;
  setLanguagesError: (error: string) => void;
  resetLanguages: () => void;
  // Initial state loading
  initFetchedLanguages: boolean;
  setInitFetchedLanguages: (fetched: boolean) => void;

  //////* Mindstring Prototypes
  mindstringPrototypes: MindstringPrototypeState;
  setMindstringPrototypes: (prototypes: MindstringPrototype[]) => void;
  setMindstringPrototypesLoading: () => void;
  setMindstringPrototypesError: (error: string) => void;
  resetMindstringPrototypes: () => void;
  // Initial state loading
  initFetchedMindstringPrototypes: boolean;
  setInitFetchedMindstringPrototypes: (fetched: boolean) => void;

  //////* Situation categories
  situationCategories: SituationCategoriesState;
  setSituationCategories: (situationCategories: SituationCategory[]) => void;
  setSituationCategoriesLoading: () => void;
  setSituationCategoriesError: (error: string) => void;
  resetSituationCategories: () => void;
  // Initial state loading
  initFetchedSituationCategories: boolean;
  setInitFetchedSituationCategories: (fetched: boolean) => void;

  //////* Prototype-to-Category mapping
  prototypeToCategoryMap: PrototypeToCategoryMapState;
  setPrototypeToCategoryMap: (
    map: Record<MindstringPrototype, SituationCategory> | null
  ) => void;
  setPrototypeToCategoryMapLoading: () => void;
  setPrototypeToCategoryMapError: (error: string) => void;
  resetPrototypeToCategoryMap: () => void;
  // Initial state loading
  initFetchedPrototypeToCategoryMap: boolean;
  setInitFetchedPrototypeToCategoryMap: (fetched: boolean) => void;
}

export const createSsotSlice: StateCreator<GlobalState, [], [], SsotSlice> = (
  set
) => ({
  //////* Languages
  languages: {
    data: [],
    isLoading: false,
    error: null,
  },
  setLanguages: (data) =>
    set({
      languages: { data, isLoading: false, error: null },
    }),
  setLanguagesLoading: () =>
    set((state) => ({
      languages: { ...state.languages, isLoading: true, error: null },
    })),
  setLanguagesError: (error) =>
    set((state) => ({
      languages: { ...state.languages, error, isLoading: false },
    })),
  resetLanguages: () =>
    set({
      languages: { data: [], isLoading: false, error: null },
    }),
  // Initial state loading
  initFetchedLanguages: false,
  setInitFetchedLanguages: (fetched) => set({ initFetchedLanguages: fetched }),

  //////* Mindstring Prototypes
  mindstringPrototypes: {
    data: [],
    isLoading: false,
    error: null,
  },
  setMindstringPrototypes: (prototypes) =>
    set({
      mindstringPrototypes: { data: prototypes, isLoading: false, error: null },
    }),
  setMindstringPrototypesLoading: () =>
    set((state) => ({
      mindstringPrototypes: {
        ...state.mindstringPrototypes,
        isLoading: true,
        error: null,
      },
    })),
  setMindstringPrototypesError: (error: string) =>
    set((state) => ({
      mindstringPrototypes: {
        ...state.mindstringPrototypes,
        error,
        isLoading: false,
      },
    })),
  resetMindstringPrototypes: () =>
    set({
      mindstringPrototypes: { data: [], isLoading: false, error: null },
    }),
  // Initial state loading
  initFetchedMindstringPrototypes: false,
  setInitFetchedMindstringPrototypes: (fetched) =>
    set({ initFetchedMindstringPrototypes: fetched }),

  //////* Situation categories
  situationCategories: {
    data: [],
    isLoading: false,
    error: null,
  },
  setSituationCategories: (categories) =>
    set({
      situationCategories: { data: categories, isLoading: false, error: null },
    }),
  setSituationCategoriesLoading: () =>
    set((state) => ({
      situationCategories: {
        ...state.situationCategories,
        isLoading: true,
        error: null,
      },
    })),
  setSituationCategoriesError: (error: string) =>
    set((state) => ({
      situationCategories: {
        ...state.situationCategories,
        error,
        isLoading: false,
      },
    })),
  resetSituationCategories: () =>
    set({
      situationCategories: { data: [], isLoading: false, error: null },
    }),
  // Initial state loading
  initFetchedSituationCategories: false,
  setInitFetchedSituationCategories: (fetched) =>
    set({ initFetchedSituationCategories: fetched }),

  //////* Prototype-to-Category mapping
  prototypeToCategoryMap: {
    data: null,
    isLoading: false,
    error: null,
  },
  setPrototypeToCategoryMap: (map) =>
    set({
      prototypeToCategoryMap: { data: map, isLoading: false, error: null },
    }),
  setPrototypeToCategoryMapLoading: () =>
    set((state) => ({
      prototypeToCategoryMap: {
        ...state.prototypeToCategoryMap,
        isLoading: true,
        error: null,
      },
    })),
  setPrototypeToCategoryMapError: (error: string) =>
    set((state) => ({
      prototypeToCategoryMap: {
        ...state.prototypeToCategoryMap,
        error,
        isLoading: false,
      },
    })),
  resetPrototypeToCategoryMap: () =>
    set({
      prototypeToCategoryMap: { data: null, isLoading: false, error: null },
    }),
  // Initial state loading
  initFetchedPrototypeToCategoryMap: false,
  setInitFetchedPrototypeToCategoryMap: (fetched) =>
    set({ initFetchedPrototypeToCategoryMap: fetched }),
});
